<script setup lang="ts">
import { computed, ref } from "vue";

import Button from "~/components/dumb/Button.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import MobileLayoutEditor from "~/components/topbar/MobileLayoutEditor.vue";
import { FilterIcon, SettingsIcon, SortIcon } from "~/icons";
import { ButtonStyle, CommandId, IconSize } from "~/shared/enums";
import { useAppStore, usePageStore, useUserStore } from "~/stores";

const appStore = useAppStore();
const userStore = useUserStore();
const pageStore = usePageStore();

const layoutEditorRef = ref<InstanceType<typeof MobileLayoutEditor> | null>(null);

const open = computed(() => userStore.getShowFilters());

const hasSort = computed(() => !!appStore.sorts.length);
const hasFilters = computed(() => !!appStore.filters.length);

const toggle = () => {
  userStore.setShowFilters(!open.value);
};
</script>

<template>
  <template v-if="!pageStore.isMobile">
    <Tooltip
      :command-id="open ? CommandId.TOGGLE_FILTERS_AND_SORTS_TO_HIDDEN : CommandId.TOGGLE_FILTERS_AND_SORTS_TO_SHOWN">
      <button
        type="button"
        class="flex h-6 items-center justify-center gap-1 rounded border p-1 text-sm font-normal text-lt border-hvy focus-ring-std"
        :class="open ? 'bg-md hover:bg-hvy' : 'hover:bg-lt'"
        aria-label="Show or hide filters and sorts"
        @click="toggle"
        @keydown.enter="toggle">
        <FilterIcon class="shrink-0 icon-sm" :class="hasFilters && 'text-primary-base dark:text-primary-base'" />
        <div v-if="hasFilters" class="select-none text-xs text-primary-base dark:text-primary-base">
          {{ appStore.filters.length }}
        </div>
        <div class="select-none text-lt">/</div>
        <SortIcon class="shrink-0 icon-sm" :class="hasSort && 'text-primary-base dark:text-primary-base'" />
        <div v-if="hasSort" class="select-none text-xs text-primary-base dark:text-primary-base">
          {{ appStore.sorts.length }}
        </div>
      </button>
    </Tooltip>
  </template>
  <template v-else>
    <Button
      :btn-style="ButtonStyle.SECONDARY"
      :icon="SettingsIcon"
      :icon-size="IconSize.S"
      a11y-label="Open layout editor"
      class="size-6"
      @click="layoutEditorRef?.open()" />
    <MobileLayoutEditor v-if="pageStore.isMobile" ref="layoutEditorRef" />
  </template>
</template>
